<script setup lang="ts">
import { format } from "date-fns";

import MedicalFormBuilder from "@/components/medicalConditionForms/MedicalFormBuilder.vue";
import Medication from "../Medication/Medication.vue";
import {
    HeartCondition, HeartSubCondition, PersonData, MedicalCondition, PrescribedConditionTypes,
} from "@/entities/conditions";
import { Suggestion } from "@/entities/suggestion";
import { DatePicker, Question, Input, Radio, RadioSet } from "@/components/fields";
import { isNumber } from "@/utils";
import { parseSchema } from "@/components/medicalConditionForms/schema";
import MedicalConditionSearch from "@/components/medicalConditionForms/MedicalConditionSearch.vue";
import { EditableList } from "@/components/EditableList";
import validateHeartForm from "./validators";
import { defaultDateFormat } from "@/constants/date";
import { useMedicalFormWithSubConditions } from "../useMedicalForm";
import heart from "./heart.json";
import { watch } from "vue";
import MedicalDiagnosisDateConflictModal
    from "@/components/medicalConditionForms/MedicalDiagnosisDateConflictModal.vue";

export interface Props {
    condition: HeartCondition,
    personDetails: PersonData,
    subConditionSuggestion: Suggestion | null
}

const props = defineProps<Props>();

const emit = defineEmits<{
        (on:"update:condition", value: HeartCondition): void
    }>();

const getInitialNewCondition = (props: Props): HeartSubCondition | null => {
    if (!props.condition.subConditions?.length) {
        return {
            syntheticId: Date.now(),
            id: Number(props.condition.id) ?? 0,
            type: PrescribedConditionTypes.Heart,
            name: props.condition.name,
            diagnosisdate: "",
            medication: [],
            occurrences: null,
            ongoingInd: null,
            code: props.condition.code ?? null,
        };
    } else if (props.subConditionSuggestion) {
        return {
            syntheticId: Date.now(),
            ...props.subConditionSuggestion,
            id: Number(props.subConditionSuggestion.id) ?? 0,
            type: PrescribedConditionTypes.Heart,
            diagnosisdate: "",
            medication: [],
            occurrences: null,
            ongoingInd: null,
            code: props.subConditionSuggestion.code ?? null,
        };
    } else {
        return null;
    }
};

const {
    onConditionUpdate,
    minSubConditionDate,
    state,
    handleUpdate,
    handleUpdateItem,
    handleSave,
} = useMedicalFormWithSubConditions<
    {type: PrescribedConditionTypes.Heart} & HeartCondition, HeartSubCondition
>({
    condition: {
        ...props.condition,
        type: PrescribedConditionTypes.Heart,
        subConditions: props.condition.subConditions || [],
        medications: props.condition.medications || [],
    },
    newCondition: getInitialNewCondition(props),
}, emit);

watch(
    () => state.newCondition?.diagnosisdate,
    (curValue, prevValue) => {
        if (
            state.newCondition &&
          "medication" in state.newCondition &&
          state.newCondition.medication.length &&
          state.prevDiagnosisDate !== curValue &&
          prevValue
        ) {
            state.hasDiagnosisDateChanged = true;
            state.prevDiagnosisDate = prevValue as unknown as string;
        }
    },
    { immediate: true },
);

const handleUndoDateChange = () => {
    if (!state.newCondition || !state.prevDiagnosisDate) {
        return;
    }
    state.hasDiagnosisDateChanged = false;
    state.newCondition.diagnosisdate = state.prevDiagnosisDate;
};

const handleRemoveMedications = () => {
    if (!state.newCondition) {
        return;
    }
    state.hasDiagnosisDateChanged = false;
    state.newCondition.medication = [];
};

const validators = validateHeartForm();

const handleConditionSelect = (condition: MedicalCondition) => {
    state.newCondition = {
        ...condition,
        type: PrescribedConditionTypes.Heart,
        syntheticId: Date.now(),
        diagnosisdate: "",
        medication: [],
        occurrences: null,
        ongoingInd: null,
    };
};

const currentDate = new Date();
const maxDate = currentDate;
const minDate = new Date(props.personDetails.dob);
</script>

<template>
    <p class="font-weight-bold-col">
        Declared heart conditions
    </p>
    <EditableList
        :current-item="state.newCondition"
        :items="condition.subConditions || []"
        :header="state.newCondition?.name"
        @update:items="handleUpdate"
        @update:item="handleUpdateItem"
        @save="handleSave"
    >
        <template
            v-for="item in condition.subConditions"
            #[item.syntheticId]
            :key="item.syntheticId"
        >
            <span
                class="col-md-2 "
            >
                <b>{{ item.name }}</b>
            </span>
        </template>
        <div
            v-if="state.newCondition !== null"
            class="border border-info rounded mt-3 p-4 medical-frame zInd2"
        >
            <p class="font-weight-bold-col">
                {{ state.newCondition.name }}
            </p>
            <Question
                name="diagnosisdate"
                label="When were you diagnosed?"
                tooltip="Please enter the date the condition was first diagnosed. If you don't know the month, please enter June as a default value."
            >
                <DatePicker
                    :key="state.newCondition.id"
                    v-model="state.newCondition.diagnosisdate"
                    :max-date="maxDate"
                    :min-date="minDate"
                    :show-day-field="false"
                    :validation="validators.diagnosisDate"
                />
            </Question>
            <template v-if="state.newCondition.diagnosisdate">
                <Question
                    name="occurrences"
                    label="How many times has this condition occurred? (optional)"
                    tooltip="How many separate episodes of this condition have you experienced.
                    If the condition happened once but is ongoing, please enter 1. If your condition is a heart attack,
                    please enter more than 1 event if the events occurred more than 3 months apart."
                >
                    <Input
                        v-model.number="state.newCondition.occurrences"
                        type="number"
                        inputmode="numeric"
                        :filter="isNumber"
                        :validation="validators.occurrences"
                    />
                </Question>
                <Question
                    name="ongoingInd"
                    label="Is this condition ongoing? (optional)"
                    tooltip="Are you still having symptoms caused by this condition? If so, answer yes, otherwise please select no."
                >
                    <RadioSet>
                        <Radio
                            v-model="state.newCondition.ongoingInd"
                            title="Yes"
                            value="1"
                        />
                        <Radio
                            v-model="state.newCondition.ongoingInd"
                            title="No"
                            value="0"
                        />
                    </RadioSet>
                </Question>
                <Medication
                    v-model="state.newCondition.medication"
                    :diagnosis-date="state.newCondition.diagnosisdate"
                />
            </template>
        </div>
    </EditableList>
    <MedicalConditionSearch
        id="heart-condition"
        label="Add another heart condition"
        :type="PrescribedConditionTypes.Heart"
        :disable-add-not-found="true"
        @select:condition="handleConditionSelect"
    />
    <MedicalFormBuilder
        v-if="minSubConditionDate !== null"
        :schema="parseSchema(heart)"
        :initial-form="state.condition"
        :diagnosis-date="format(minSubConditionDate, defaultDateFormat)"
        @update:form="onConditionUpdate"
    />
    <MedicalDiagnosisDateConflictModal
        :handle-undo-date-change="handleUndoDateChange"
        :has-diagnosis-date-changed="state.hasDiagnosisDateChanged"
        :handle-remove-medications="handleRemoveMedications"
    />
</template>

<style>

</style>
